import { useEffect, useId, useState } from 'react';
import { AgxSolicitorDetails } from '@urbanx/agx-ui-components';
import {
  SolicitorDetails,
  useInvalidateUseStaffGetBuyersSolicitor,
  useStaffGetBuyersSolicitor,
  useStaffUpdateBuyersSolicitor,
} from '@urbanx/agx.campaigns.client';
import { useSelectedCampaign, useSelectedContract } from 'hooks';
import { MenuTitles, Pages } from 'constants/menu';
import { ErrorModal } from 'helpers/ErrorModal';
import { ContractSubPage } from '../ContractSubPage';
import TerminateContractAction from '../TerminateContractAction';
import { getBuyerDetailsSubPages } from './BuyerDetailsSubPages';
import { isDirtyEqual } from 'utils/compares';
import { SubPagePanel } from 'components/panel/SubPagePanel';
import { RexDisconnectedModalFields } from 'pages/campaigns/components/manageCampaign/RexDataUpdateHelper';
import { useGetListingByCampaignId } from '@urbanx/agx.rex.client';
import { isEqual } from 'lodash-es';
import useRexDisconnectedModal from 'pages/campaigns/components/manageCampaign/useRexDisconnectedModal';

const BuyersSolicitor = () => {
  const solicitorId = useId();
  const { campaign } = useSelectedCampaign();
  const { contract } = useSelectedContract();
  const [isLoaded, setIsLoaded] = useState(false);
  const [validate, setValidate] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [submitFormErrors, setSubmitFormErrors] = useState<string[]>([]);
  const invalidateUseStaffGetBuyersSolicitor = useInvalidateUseStaffGetBuyersSolicitor();

  const {
    data: buyerSolicitor,
    isPending,
    isError,
  } = useStaffGetBuyersSolicitor(contract?.contractId || '', !!contract?.contractId);

  const [value, setValue] = useState<SolicitorDetails | undefined>(buyerSolicitor);
  const { modal: rexDisconnectedModal, openWithChanges } = useRexDisconnectedModal();

  useEffect(() => {
    setValue(buyerSolicitor);
    setIsLoaded(!isPending);
  }, [buyerSolicitor, isPending]);

  useEffect(() => {
    setValidate(!isDirtyEqual(value, buyerSolicitor));
  }, [value]);

  const { staffUpdateBuyersSolicitor, staffUpdateBuyersSolicitorPending } =
    useStaffUpdateBuyersSolicitor({
      onSuccess: () => {},
      onError: () => {},
    });

  const { data: rexListing, isPending: isPendingRexListing } = useGetListingByCampaignId(
    campaign?.campaignId || '',
    campaign?.campaignId !== undefined
  );

  const isFormDirty = !isDirtyEqual(value, buyerSolicitor);

  const onSave = async (success: () => void) => {
    if (!value || !contract?.contractId) return;

    if (!rexListing && isPendingRexListing === false) {
      const prevSolicitor = buyerSolicitor;
      const newSolicitor = value;

      openWithChanges(
        !isEqual(prevSolicitor, newSolicitor)
          ? [
              {
                field: RexDisconnectedModalFields.BuyerSolicitor,
                value: newSolicitor?.solicitor?.solicitorName || '',
              },
            ]
          : []
      );
    }

    await staffUpdateBuyersSolicitor(
      {
        body: value,
        contractId: contract.contractId,
      },
      {
        onSuccess: async (data) => {
          if (!contract?.contractId) return;
          if (data?.success) {
            success();
            await invalidateUseStaffGetBuyersSolicitor(contract.contractId);
          } else {
            setSubmitFormErrors(data?.errors || []);
            setOpenErrorModal(true);
          }
        },
      }
    );
  };

  if (!isLoaded) return;

  return (
    <ContractSubPage
      key={value ? solicitorId : undefined}
      pageTitle="Buyer Details"
      secondaryActiveMenu={MenuTitles.BUYERS_DETAILS}
      currentPage={Pages.BUYERS_SOLICITOR}
      subPages={getBuyerDetailsSubPages(campaign?.state)}
      onSaveChanges={onSave}
      isFormDirty={isFormDirty}
      isSaving={staffUpdateBuyersSolicitorPending}
      isError={isError}
      secondaryActions={<TerminateContractAction />}
    >
      {!isPending && value && (
        <SubPagePanel panelTitle="Buyer's Solicitor" expandable defaultExpanded>
          <AgxSolicitorDetails
            id="BuyersSolicitorDetails"
            onValueUpdated={({ value }) => setValue(value)}
            defaultValue={value}
            label=""
            solicitorLabel="Solicitor"
            required
            validate={validate}
          />
        </SubPagePanel>
      )}
      {openErrorModal && (
        <ErrorModal errorList={submitFormErrors} setShowModal={setOpenErrorModal} />
      )}
      {rexDisconnectedModal}
    </ContractSubPage>
  );
};

export default BuyersSolicitor;
